import * as styles from "./DogBioLinks.module.scss";

import {
	Button,
	SocialMediaIcons,
	type SocialMediaIconsProps,
} from "../../components/common";
import { Heart, HeartFilled } from "../common/Icons";

import { navigate } from "gatsby";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { useMyAccountUK } from "../../context";
import { useFavouriteDog } from "../../hooks/useFavouriteDog";
import { Colours } from "../../utils/colours";
import { setDogToEnquireCookie } from "../../utils/cookies";
import { pushToDataLayer } from "../../utils/google";
import { isSpecificSite } from "../../utils/isSpecificSite";
import { getDomain } from "../../utils/url";
import SSRSuspense from "../SSRSuspense/SSRSuspense";

export interface DogBioLinksProps
	extends DogsTrust.DogData,
		SocialMediaIconsProps {
	adoptionFormUrl: string;
	dogDataTracking: DogsTrust.DogDataTracking;
	dog: DogsTrust.DogData;
}

export const DogBioLinks: React.FC<DogBioLinksProps> = ({
	dog,
	xUrl,
	facebookUrl,
	linkedInUrl,
	mailToUrl,
	whatsAppUrl,
	adoptionFormUrl,
	dogDataTracking,
}) => {
	isSpecificSite("uk", "DogBioLinks");

	const { isFav, onClick } = useFavouriteDog(dog);
	const { handleEnquireAboutDog, isAuthenticated, profile } = useMyAccountUK();

	const [modalIsOpen, setModalIsOpen] = useState(false);

	const openModal = useCallback(() => {
		setModalIsOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setModalIsOpen(false);
	}, []);

	const customStyles = {
		content: {
			padding: "50px 30px",
			top: "50%",
			left: "50%",
			right: "auto",
			bottom: "auto",
			marginRight: "-50%",
			transform: "translate(-50%, -50%)",
			border: "0",
			borderRadius: "10px",
			boxShadow: "0px 4px 34px 0px rgba(0, 0, 0, 0.25)",
			maxHeight: "80vh",
			maxWidth: "75vw",
			backgroundColor: "#faf5e6",
		},
		overlay: {
			zIndex: "9999",
		},
	};

	useEffect(() => {
		if (modalIsOpen) {
			document.getElementsByTagName("body")[0].style.overflow = "hidden";
		} else {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		}
		return () => {
			document.getElementsByTagName("body")[0].style.overflow = "scroll";
		};
	}, [modalIsOpen]);

	const onEnquireClick = useCallback(async () => {
		if (process.env.GATSBY_FEATURE_FLAG_ADOPTION_FORM === "true") {
			if (isAuthenticated) {
				const isInterested = profile?.interested
					?.map((dog) => dog?.id)
					.includes(dog?.id);
				if (!isInterested) {
					handleEnquireAboutDog(dog, dogDataTracking);
				}
				navigate("/my-account/rehoming");
			} else {
				// open modal to create an account/log in.
				// If cancel just return to page without anything happening
				// If login then create cookie and redirect back to my-account/rehoming page and handleEnquireAboutDog on that page
				openModal();
			}
		} else {
			window.open(adoptionFormUrl, "_blank");
		}
	}, [
		isAuthenticated,
		handleEnquireAboutDog,
		dog,
		dogDataTracking,
		adoptionFormUrl,
		profile,
		openModal,
	]);
	return (
		<>
			<div className={styles.buttons}>
				<Button
					className={styles.biolinkbutton}
					fullWidth={true}
					onClick={onEnquireClick}
				>
					Start your application
				</Button>
				<Button
					className={styles.biolinkbutton}
					variant="tertiary"
					fullWidth={true}
					onClick={() => onClick(dogDataTracking)}
					IconLeft={() =>
						isFav ? (
							<HeartFilled fill={Colours.MIDNIGHT} />
						) : (
							<Heart fill={Colours.MIDNIGHT} />
						)
					}
				>
					{isFav ? "Added to favourites" : "Add to favourites"}
				</Button>
			</div>
			{(xUrl || facebookUrl || mailToUrl || linkedInUrl) && (
				<div className={styles.socmed}>
					<span className={styles.socmedtitle}>{"HELP ME FIND A HOME"}</span>
					<SocialMediaIcons
						className={styles.socmedicons}
						invert={true}
						xUrl={xUrl}
						facebookUrl={facebookUrl}
						mailToUrl={mailToUrl}
						linkedInUrl={linkedInUrl}
						whatsAppUrl={whatsAppUrl}
					/>
				</div>
			)}
			<SSRSuspense>
				<Modal
					isOpen={modalIsOpen}
					style={customStyles}
					preventScroll={true}
					shouldCloseOnEsc={true}
					shouldCloseOnOverlayClick={true}
					shouldFocusAfterRender={true}
					shouldReturnFocusAfterClose={true}
					ariaHideApp={false}
				>
					<div className={styles.modalcontainer}>
						<div>
							<h3>Create an account to register your interest in adoption.</h3>
						</div>
						<Button
							fullWidth={true}
							variant="primary"
							onClick={() => {
								pushToDataLayer({
									event: "button_navigation",
									button_clicked: "create an account - modal",
								});
								setDogToEnquireCookie(dog, dogDataTracking);
								window.location.href = `${
									process.env.GATSBY_PROFILE_SIGN_IN_URL
								}?ptrt=${getDomain()}/my-account/rehoming`;
							}}
						>
							Create an account
						</Button>
						<Button
							onClick={closeModal}
							onKeyDown={(e) => e.key === "Enter" && closeModal}
							fullWidth={true}
							variant="secondary"
						>
							Not just yet
						</Button>
						<p className={styles.login}>
							Already have an account?{" "}
							<button
								className={styles.loginlink}
								type="button"
								onClick={() => {
									pushToDataLayer({
										event: "button_navigation",
										button_clicked: "log in to account - modal",
									});
									setDogToEnquireCookie(dog, dogDataTracking);
									window.location.href = `${
										process.env.GATSBY_PROFILE_SIGN_IN_URL
									}?ptrt=${getDomain()}/my-account/rehoming`;
								}}
							>
								Log in here
							</button>
						</p>
					</div>
				</Modal>
			</SSRSuspense>
		</>
	);
};

export const DogBioLinksCard: React.FC<DogBioLinksProps> = ({
	id,
	dog,
	name,
	adoptionFormUrl,
	...rest
}) => {
	return (
		<div className={styles.dogbiolinkscontainer}>
			<h2 className={styles.title}>{`Apply to adopt a dog like ${name}`}</h2>

			<DogBioLinks
				id={id}
				dog={dog}
				name={name}
				{...rest}
				adoptionFormUrl={adoptionFormUrl}
			/>
		</div>
	);
};

export default DogBioLinksCard;
